import React, { useContext, useState } from 'react';
import {
  useRedeemToken,
  ValidationCode,
  ValidationMsg,
} from '../hooks/useRedeemToken';
import { useSearchParams } from 'react-router-dom';
import { FormStatus } from '../util/app.types';
import { Tr8Context } from '../App';
import ValidationMessage from '../components/ValidationMessage';
import { Tr8 } from '../translations/tr8';

enum PasswordValidationStatus {
  Ok = 'Ok',
  DontMatch = 'DontMatch',
  BothBlank = 'BothBlank',
  PreviouslyUsed = 'PreviouslyUsed',
  InvalidLength = 'InvalidLength',
}

export enum RedeemTokenTypes {
  PasswordReset = 'PASSWORD_RESET',
  NewUser = 'NEW_USER',
  EmailConfirmation = 'EMAIL_CONFIRMATION',
}

const constructValidationMessage = (msg: ValidationMsg, tr8: Tr8) => {
  switch (msg.code) {
    case ValidationCode.length:
      return tr8(
        'change.password.short',
        new Map([['MIN_LENGTH', String(msg.extra)]]),
      );
    case ValidationCode.reuse:
      return tr8(
        'change.password.reuse',
        new Map([['NUM_CHECKED_PASSWORDS', String(msg.extra)]]),
      );
    case ValidationCode.digit:
      return tr8('change.password.digit.required');
    case ValidationCode.mixedcase:
      return tr8('change.password.mixed.case.required');
    default:
      return tr8('something-went-wrong');
  }
};

type Props = {
  tokenType: RedeemTokenTypes;
};

function getInvalidTokenMessage(tr8: Tr8, tokenType: RedeemTokenTypes): string {
  switch (tokenType) {
    case RedeemTokenTypes.PasswordReset:
      return tr8('invalid-reset-token');
    case RedeemTokenTypes.NewUser:
      return tr8('invalid-new-user-token');
    default:
      // not expected case
      return tr8('something-went-wrong');
  }
}

export const RedeemToken = ({ tokenType }: Props): JSX.Element => {
  const tr8 = useContext(Tr8Context);
  const [searchParams] = useSearchParams();
  const { formStatus, dispatch, validationMessage } = useRedeemToken(
    searchParams,
    tokenType,
  );
  const [passwordValidationStatus, setPasswordValidationStatus] = useState(
    PasswordValidationStatus.Ok,
  );

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // No trimming (for consistency with other password fields)
    const p1 = (e.currentTarget.elements[0] as HTMLInputElement).value;
    const p2 = (e.currentTarget.elements[1] as HTMLInputElement).value;
    if (p1 !== p2) {
      setPasswordValidationStatus(PasswordValidationStatus.DontMatch);
      return;
    } else if (!p1) {
      setPasswordValidationStatus(PasswordValidationStatus.BothBlank);
      return;
    } else {
      dispatch(p1);
    }
  };

  const resetValidation = () => {
    setPasswordValidationStatus(PasswordValidationStatus.Ok);
  };

  const validationMsg = validationMessage
    ? constructValidationMessage(validationMessage, tr8)
    : null;

  const showForm =
    formStatus === FormStatus.Editing || formStatus === FormStatus.InFlight;

  return (
    <>
      {showForm && (
        <form
          onSubmit={handleSubmit}
          onChange={resetValidation}
          className="formContainer"
        >
          <div className="intro-text">{tr8('change-password-intro-text')}</div>
          <input
            data-test-id="new-password"
            type="password"
            id="new-password"
            name="new-password"
            placeholder="password"
            autoComplete="off"
            className="form-field"
            disabled={formStatus === FormStatus.InFlight}
          ></input>
          <input
            data-test-id="password-confirm"
            type="password"
            id="password-confirm"
            name="password-confirm"
            placeholder="password"
            autoComplete="off"
            className="form-field"
            disabled={formStatus === FormStatus.InFlight}
          ></input>
          <input
            data-test-id="password-submit"
            type="submit"
            value="Submit"
            className="submit-button"
          />
          {passwordValidationStatus === PasswordValidationStatus.DontMatch && (
            <ValidationMessage msg={tr8('confirm-password-different')} />
          )}
          {passwordValidationStatus === PasswordValidationStatus.BothBlank && (
            <ValidationMessage msg={tr8('both-passwords-blank')} />
          )}
          {validationMsg && <ValidationMessage msg={validationMsg} />}
        </form>
      )}
      {formStatus === FormStatus.Failed && (
        <form className="formContainer">
          <span data-test-id="status-text">{tr8('something-went-wrong')}</span>
        </form>
      )}
      {formStatus === FormStatus.Succeeded && (
        <form className="formContainer">
          <span data-test-id="status-text">{tr8('close-browser')}</span>
        </form>
      )}
      {formStatus === FormStatus.InvalidToken && (
        <form className="formContainer">
          <span data-test-id="status-text">
            {getInvalidTokenMessage(tr8, tokenType)}
          </span>
        </form>
      )}
      {formStatus === FormStatus.ExpiredNewUserToken && (
        <form className="formContainer">
          <span data-test-id="status-text">
            {tr8('expired-new-user-token')}
          </span>
        </form>
      )}
    </>
  );
};
